import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Receipt, FilterX, IndianRupee, Clock, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "../AuthContext";
import api, { apiError, fmtDate, downloadBlob, istToday } from "../api";
import { Card, PageHeader, Button, Input, Select, Table, EmptyState, Spinner, Badge, StatusBadge, Modal, Field } from "../components/ui";

const todayStr = () => istToday();

function SummaryCard({ icon: Icon, label, value, color, bg, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className={`mb-3 inline-flex rounded-md p-2 ${bg}`}><Icon size={18} className={color} /></div>
      <p className="font-heading text-2xl font-extrabold text-slate-900">{value}</p>
      <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    </Card>
  );
}

export default function Billing() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [filters, setFilters] = useState({
    client_id: "", clinic_id: "", date_from: todayStr(), date_to: todayStr(),
    payment_status: "", case_status: "",
  });
  const [partialTarget, setPartialTarget] = useState(null);
  const [partialAmount, setPartialAmount] = useState("");

  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));

  const load = useCallback(() => {
    api.get("/billing", { params }).then((r) => setData(r.data)).catch(() => setData({ rows: [], total_amount: 0, outstanding_amount: 0 }));
    api.get("/billing/summary", { params }).then((r) => setSummary(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [JSON.stringify(params)]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/clinics").then((r) => setClinics(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });

  const savePayment = async (visitId, status, amount) => {
    try {
      await api.put(`/billing/visits/${visitId}/payment-status`, { payment_status: status, amount_paid: amount });
      toast.success("Payment status updated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const changePayment = (row, status) => {
    if (status === row.payment_status) return;
    if (status === "partially_paid") {
      setPartialTarget(row);
      setPartialAmount(row.amount_paid > 0 ? String(row.amount_paid) : "");
    } else {
      savePayment(row.visit_id, status, null);
    }
  };

  const submitPartial = async (e) => {
    e.preventDefault();
    const amt = parseFloat(partialAmount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    await savePayment(partialTarget.visit_id, "partially_paid", amt);
    setPartialTarget(null);
  };

  const exportFile = async (kind) => {
    try {
      await downloadBlob(`/billing/export/${kind}`, kind === "excel" ? "billing_report.xlsx" : "billing_report.pdf", params);
      toast.success("Report downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };

  const periodLabel = filters.date_from === todayStr() && filters.date_to === todayStr()
    ? "Today" : `${filters.date_from || "start"} → ${filters.date_to || "now"}`;

  return (
    <div>
      <PageHeader title="Billing & Revenue" subtitle={`Showing: ${periodLabel} — change the date range to see other periods`}>
        <Button variant="outline" onClick={() => exportFile("excel")} data-testid="export-excel-btn"><FileSpreadsheet size={15} /> Excel</Button>
        <Button variant="outline" onClick={() => exportFile("pdf")} data-testid="export-pdf-btn"><FileText size={15} /> PDF</Button>
      </PageHeader>

      <div className="stagger grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard icon={IndianRupee} label={`Billed (${periodLabel})`} value={summary ? `₹${summary.total_amount.toFixed(2)}` : "-"} color="text-navy" bg="bg-navy/10" testid="summary-total" />
        <SummaryCard icon={Clock} label={`Outstanding (${periodLabel})`} value={summary ? `₹${summary.outstanding_amount.toFixed(2)}` : "-"} color="text-amber-600" bg="bg-amber-50" testid="summary-outstanding" />
        <SummaryCard icon={CheckCircle2} label="Completed Cases" value={summary ? summary.completed_cases : "-"} color="text-emerald-600" bg="bg-emerald-50" testid="summary-completed" />
        <SummaryCard icon={XCircle} label="Incomplete Cases" value={summary ? summary.incomplete_cases : "-"} color="text-red-600" bg="bg-red-50" testid="summary-incomplete" />
      </div>

      <Card className="mt-6 p-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <Select value={filters.client_id} onChange={set("client_id")} data-testid="billing-client-filter">
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select value={filters.clinic_id} onChange={set("clinic_id")} data-testid="billing-clinic-filter">
            <option value="">All clinics</option>
            {clinics.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Input type="date" value={filters.date_from} onChange={set("date_from")} data-testid="billing-date-from" />
          <Input type="date" value={filters.date_to} onChange={set("date_to")} data-testid="billing-date-to" />
          <Select value={filters.payment_status} onChange={set("payment_status")} data-testid="billing-payment-filter">
            <option value="">All payments</option>
            <option value="pending">Payment Pending</option>
            <option value="partially_paid">Partially Paid</option>
            <option value="paid">Paid</option>
          </Select>
          <Select value={filters.case_status} onChange={set("case_status")} data-testid="billing-case-filter">
            <option value="">All cases</option>
            <option value="completed">Completed</option>
            <option value="incomplete">Incomplete</option>
          </Select>
        </div>
        <div className="mt-3 flex items-center gap-4">
          <button
            onClick={() => setFilters({ client_id: "", clinic_id: "", date_from: todayStr(), date_to: todayStr(), payment_status: "", case_status: "" })}
            className="flex items-center gap-1 text-sm font-semibold text-slate-500 transition-colors hover:text-navy"
            data-testid="billing-today-btn"
          >
            Today
          </button>
          <button
            onClick={() => setFilters({ client_id: "", clinic_id: "", date_from: "", date_to: "", payment_status: "", case_status: "" })}
            className="flex items-center gap-1 text-sm font-semibold text-slate-500 transition-colors hover:text-navy"
            data-testid="billing-clear-filters"
          >
            <FilterX size={14} /> All time
          </button>
        </div>
      </Card>

      <Card className="mt-6">
        {data === null ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : data.rows.length === 0 ? (
          <EmptyState text="No billing records match your filters" />
        ) : (
          <>
            <Table headers={["Candidate", "Client", "Clinic", "Services", "Total (₹)", "Outstanding (₹)", "Case Status", "Payment", "Actions"]}>
              {data.rows.map((r) => {
                const payLocked = r.payment_status === "paid" && user.role !== "admin";
                return (
                  <tr key={r.visit_id} className="border-b border-slate-100 transition-colors hover:bg-slate-50" data-testid="billing-row">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{r.candidate_name}</p>
                      <p className="text-xs text-slate-400">{r.rank} · {r.company_id} · {fmtDate(r.created_at)}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{r.client_name}</td>
                    <td className="px-4 py-3 text-slate-600">{r.clinic_name}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{r.service_names || "-"}</td>
                    <td className="px-4 py-3 font-bold text-slate-900">₹{r.total.toFixed(2)}</td>
                    <td className="px-4 py-3 font-semibold text-amber-600">₹{r.outstanding.toFixed(2)}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3">
                      <Select
                        value={r.payment_status}
                        onChange={(e) => changePayment(r, e.target.value)}
                        disabled={payLocked}
                        className="!py-1.5 text-xs"
                        data-testid={`payment-select-${r.visit_id}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="partially_paid">Partially Paid</option>
                        <option value="paid">Paid</option>
                      </Select>
                      {r.payment_status === "partially_paid" && (
                        <p className="mt-1 text-[11px] font-semibold text-teal">₹{r.amount_paid.toFixed(2)} paid</p>
                      )}
                      {payLocked && <p className="mt-1 text-[10px] text-slate-400">Locked — Admin only</p>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button
                          onClick={() => downloadBlob(`/billing/visits/${r.visit_id}/invoice`, `invoice_${r.candidate_name}.pdf`)}
                          className="rounded p-1.5 text-slate-500 transition-colors hover:bg-teal-light hover:text-teal"
                          title="Download invoice (PDF)"
                          data-testid={`invoice-btn-${r.visit_id}`}
                        >
                          <Receipt size={15} />
                        </button>
                        <button
                          onClick={() => downloadBlob(`/billing/visits/${r.visit_id}/excel`, `billing_${r.candidate_name}.xlsx`)}
                          className="rounded p-1.5 text-slate-500 transition-colors hover:bg-emerald-50 hover:text-emerald-600"
                          title="Download billing (Excel)"
                          data-testid={`candidate-excel-btn-${r.visit_id}`}
                        >
                          <FileSpreadsheet size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </Table>
            <div className="flex justify-end gap-8 border-t border-slate-200 px-6 py-4 text-sm">
              <p className="text-slate-500">Outstanding: <span className="font-bold text-amber-600">₹{data.outstanding_amount.toFixed(2)}</span></p>
              <p className="text-slate-500">Total: <span className="font-bold text-navy">₹{data.total_amount.toFixed(2)}</span></p>
            </div>
          </>
        )}
      </Card>

      {summary && (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {[["Client-wise Billing", summary.client_wise, "client-wise-table"], ["Clinic-wise Billing", summary.clinic_wise, "clinic-wise-table"]].map(([title, rows, tid]) => (
            <Card key={title} data-testid={tid}>
              <h3 className="border-b border-slate-200 px-6 py-4 font-heading text-lg font-bold text-slate-900">{title} <span className="text-xs font-normal text-slate-400">({periodLabel})</span></h3>
              {rows.length === 0 ? (
                <EmptyState text="No data for this period" />
              ) : (
                <Table headers={["Organization", "Cases", "Total (₹)", "Outstanding (₹)"]}>
                  {rows.map((r) => (
                    <tr key={r.name} className="border-b border-slate-100">
                      <td className="px-4 py-3 font-semibold text-slate-900">{r.name}</td>
                      <td className="px-4 py-3 text-slate-600">{r.cases}</td>
                      <td className="px-4 py-3 text-slate-700">₹{r.total.toFixed(2)}</td>
                      <td className="px-4 py-3 font-semibold text-amber-600">₹{r.outstanding.toFixed(2)}</td>
                    </tr>
                  ))}
                </Table>
              )}
            </Card>
          ))}
        </div>
      )}

      <Modal open={!!partialTarget} onClose={() => setPartialTarget(null)} title={`Partial Payment — ${partialTarget?.candidate_name || ""}`}>
        {partialTarget && (
          <form onSubmit={submitPartial} className="space-y-4">
            <p className="text-sm text-slate-600">Total billed: <span className="font-bold text-navy">₹{partialTarget.total.toFixed(2)}</span></p>
            <Field label="Amount Paid (₹)" required>
              <Input type="number" step="0.01" min="0.01" max={partialTarget.total} value={partialAmount}
                     onChange={(e) => setPartialAmount(e.target.value)} required autoFocus data-testid="partial-amount-input" />
            </Field>
            <Button type="submit" className="w-full" data-testid="partial-amount-submit-btn">Save Partial Payment</Button>
          </form>
        )}
      </Modal>
    </div>
  );
}
